import {Action, getModule, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {
    Cart,
    CartItem,
    CartResponse,
    ProfessionalDisclaimers,
    QuoteAddress,
    Totals,
    UpdateCartRequest,
} from '@/store/models';
import {addWishlist, deleteCartItem, getCart, getTotals, updateCartQty} from '@/store/api';

import store from '@/store';
import {blankState, initState} from '@/store/stateinit';
import debounce from '@/commons/debounce';
import checkout from '@/store/modules/checkout';
import {bus} from '@/events/bus';
import receiveEvent from '@/events/cartReceived';
import to from 'await-to-js';
import handleError from '@/commons/errorHandler';
import {consumeAllMessages, displayCookieMessages} from '@/commons/cookies';
import {addExtensionAttributes} from '@/commons/addressNormalizer';

@Module({
    namespaced: true,
    name: 'cart',
    store,
    dynamic: true,
})
class CartModule extends VuexModule {
    public cart: Cart = initState('cart');
    public totals: Totals = initState('totals');
    public cartToken: string = initState('cartToken');
    public professionalDisclaimers: ProfessionalDisclaimers = initState('professionalDisclaimers');
    public loadingFlags: any = {totals: false, shipMethods: false, cap: false, giftcard: false};

    get token(): string {
        return this.cartToken;
    }

    get isEmpty(): boolean {
        return this.cart.items.length === 0;
    }

    get cartHasError(): boolean {
        return !this.cart.items.every((val: any) => {
            return val.extension_attributes.extitem_error === '';
        });
    }

    // eslint-disable-next-line consistent-return
    get shippingAssignementAddress(): QuoteAddress | void {
        if (this.cart.extension_attributes && this.cart.extension_attributes.shipping_assignments) {
            const assignements = this.cart.extension_attributes.shipping_assignments;

            if (Array.isArray(assignements)) {
                let found;

                assignements.forEach((assignement) => {
                    if (assignement.shipping && assignement.shipping.address) {
                        found = assignement.shipping.address;
                    }
                });

                if (found) {
                    return found;
                }
            }
        }
    }

    // eslint-disable-next-line consistent-return
    get shippingAssignement(): any | void {
        if (this.cart.extension_attributes && this.cart.extension_attributes.shipping_assignments) {
            const assignements = this.cart.extension_attributes.shipping_assignments;

            if (Array.isArray(assignements) && assignements.length > 0) {
                return assignements[0];
            }
        }
    }

    get hasGiftcard(): boolean {
        const giftSkus = store.state.options.gift.skus.split(',');

        return this.cart.items.some((item) => {
            return giftSkus.indexOf(item.sku) !== -1;
        });
    }

    get giftCardItems(): CartItem[] {
        const items: CartItem[] = [];

        this.cart.items.forEach((item) => {
            if (item.product_type === 'giftcard') {
                items.push(item);
            }
        });

        return items;
    }

    get isOnlyGiftCard(): boolean {
        return this.cart.items.length > 0 && this.cart.items.length - this.giftCardItems.length <= 0;
    }

    get isVirtual(): boolean {
        return this.cart.is_virtual || false;
    }

    get isProfessional(): boolean {
        let isProfessional = false;

        this.cart.items.forEach((item) => {
            if (item.extension_attributes.extis_professional) {
                isProfessional = true;
            }
        });

        return isProfessional;
    }

    get grandTotal(): number {
        const grandTotal = this.totals.total_segments.find((total) => {
            return total.code === 'grand_total';
        });

        return grandTotal ? grandTotal.value : 0;
    }

    get storeId(): number {
        return this.cart.store_id;
    }

    @Mutation
    public setToken(token: string) {
        this.cartToken = token;
    }

    @Mutation
    public flagLoading(payload: {key: string; value: boolean}) {
        this.loadingFlags[payload.key] = payload.value;
    }

    @Mutation
    public setCart(cart: Cart) {
        this.cart = cart;
    }

    @Mutation
    public setTotals(totals: Totals) {
        this.totals = totals;
    }

    @Mutation
    public setQty(payload: {itemId: number; qty: number}) {
        const item = this.cart.items.find((i) => i.item_id === payload.itemId);

        if (item) {
            item.qty = payload.qty;
        }

        let sum = 0;

        this.cart.items.forEach((i: CartItem) => {
            sum += i.extension_attributes.extprice * i.qty;
        });

        this.totals.subtotal = sum;
    }

    @Mutation
    public deleteItem(itemId: number) {
        const idx = this.cart.items.findIndex((i: CartItem) => i.item_id === itemId);
        this.cart.items.splice(idx, 1);

        let sum = 0;

        this.cart.items.forEach((i: CartItem) => {
            sum += i.extension_attributes.extprice * i.qty;
        });

        this.totals.subtotal = sum;
    }

    @Mutation
    public resetItemError(itemId: number) {
        const item = this.cart.items.find((i) => i.item_id === itemId);

        if (item) {
            item.extension_attributes.extitem_error = '';
        }
    }

    @Mutation
    public reset() {
        this.cart = blankState('cart');
        this.totals = blankState('totals');
        this.cartToken = '';
        this.loadingFlags = {totals: false};
    }

    @Action
    public async getCart() {
        const cart: CartResponse = await getCart();
        if (cart.customer && cart.customer.addresses) {
            cart.customer.addresses.forEach((address, index, list) => {
                list[index] = addExtensionAttributes(address);
            });
        }
        this.setCart(cart);
        bus.$emit(receiveEvent, cart);
    }

    @Action
    public async getTotals() {
        this.flagLoading({key: 'totals', value: true});
        const [err, resp]: any = await to(getTotals());
        this.flagLoading({key: 'totals', value: false});

        return new Promise((resolve, reject) => {
            if (err) {
                reject(err);
            } else {
                this.setTotals(resp as Totals);
                resolve(resp);
            }
        });
    }

    @Action
    @debounce(500)
    public async updateQty(payload: {item: CartItem; oldQty: number}) {
        const data: UpdateCartRequest = {
            cartItem: {
                qty: payload.item.qty,
                quote_id: this.token.toString() !== '' ? this.token : this.cart.id,
                sku: payload.item.sku,
            },
        };

        this.flagLoading({key: 'totals', value: true});
        const [err, resp]: any = await to(updateCartQty(data, payload.item.item_id));
        this.flagLoading({key: 'totals', value: false});

        return new Promise((resolve, reject) => {
            if (err) {
                this.setQty({itemId: payload.item.item_id, qty: payload.oldQty});
                handleError(err, false);
                reject(err);
            } else {
                this.getCart();
                this.getTotals();
                checkout.estimateShipping(checkout.defaultShippingAddress);
                checkout.getPaymentMethods(
                    checkout.billingSameAsShipping ? checkout.shippingAddress : checkout.billingAddress,
                    checkout.shippingAddress
                );
                displayCookieMessages();
                consumeAllMessages();
                this.resetItemError(payload.item.item_id);
                resolve(resp);
            }
        });
    }

    @Action
    public async deleteCartItem(itemId: number) {
        try {
            const resp = await deleteCartItem(itemId);
            if (resp) {
                this.deleteItem(itemId);
            }
            await this.getCart();
            this.getTotals();

            checkout.estimateShipping(checkout.defaultShippingAddress);
            checkout.getPaymentMethods(
                checkout.billingSameAsShipping ? checkout.shippingAddress : checkout.billingAddress,
                checkout.shippingAddress
            );
            displayCookieMessages();
            consumeAllMessages();
        } catch (e) {
            handleError(e);
        }
    }

    @Action
    public async deleteGiftCardItems() {
        const promises: Array<Promise<any>> = [];
        this.giftCardItems.forEach((item) => {
            promises.push(deleteCartItem(item.item_id));
        });

        return new Promise((resolve, reject) => {
            Promise.all(promises)
                .then((res) => {
                    this.getTotals();
                    checkout.estimateShipping(checkout.defaultShippingAddress);
                    checkout.getPaymentMethods(
                        checkout.billingSameAsShipping ? checkout.shippingAddress : checkout.billingAddress,
                        checkout.shippingAddress
                    );
                    resolve(res);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    @Action
    public async addToWishlist(sku: string) {
        try {
            const result = await addWishlist(sku);
            if (result === false) {
                return result;
            }
            return true;
        } catch (e) {
            handleError(e);
        }
        return '';
    }
}

export default getModule(CartModule);
